<template>
  <article
    class="member_coupon"
    :class="{ coupon_used: hasStamp, is_clickable: canShowDetail }"
    role="link"
    :tabindex="canShowDetail ? 0 : -1"
    :aria-disabled="!canShowDetail"
    :aria-label="`查看${item.title}詳細資料`"
    @click="onCardClick"
    @keydown="onCardKeydown"
  >
    <couponImage :image="item.image" variant="thumbnail" />
    <div class="member_coupon_content">
      <h3 class="text_two_lines">{{ item.title }}</h3>
      <p v-if="hasStatusNote" class="coupon_status_note">{{ getStatusText }}</p>
      <p v-if="isSelectedWithOrder" class="coupon_status_note">
        占用訂單識別碼：{{ item.selected_order_uuid }}
      </p>
      <p class="coupon_period">
        使用期限<br />{{ getValidFromText }} ～ {{ getValidUntilText }}
      </p>
    </div>
    <button
      v-if="!hasStamp"
      class="coupon_action"
      type="button"
      :disabled="!canUse || !canShowDetail"
      @click.stop="showMemberDetail"
    >
      使用
    </button>
    <img
      v-if="isUsed"
      class="used_stamp"
      src="@/assets/icon-used.png"
      alt="已使用"
    />
    <img
      v-else-if="isExpired"
      class="used_stamp"
      src="@/assets/icon-expired.png"
      alt="已失效"
    />
  </article>
</template>
<script>
import couponImage from "@/components/coupon/couponImage.vue";
import {
  canUseCoupon,
  formatCouponDateTime,
  getCouponStatus,
  getCouponStatusText,
  getCouponValidFromAt,
  getCouponValidUntilAt,
} from "@/utils/coupon.utils";
export default {
  components: { couponImage },
  props: {
    item: { type: Object, required: true },
    isBusy: { type: Boolean, default: false },
    nowMs: { type: Number, required: true },
  },
  emits: ["showMemberDetail"],
  computed: {
    canShowDetail() {
      return (
        !this.isBusy &&
        typeof this.item.member_coupon_uuid === "string" &&
        this.item.member_coupon_uuid.trim().length > 0
      );
    },
    isUsed() {
      return getCouponStatus(this.item, this.nowMs) === "used";
    },
    // expired／revoked／disabled_by_template 對外都顯示「已失效」
    isExpired() {
      return this.getStatusText === "已失效";
    },
    // 已使用、已失效改以灰底＋戳章呈現，不顯示使用按鈕
    hasStamp() {
      return this.isUsed || this.isExpired;
    },
    canUse() {
      return canUseCoupon(this.item, this.nowMs);
    },
    getStatusText() {
      return getCouponStatusText(this.item, this.nowMs);
    },
    isSelectedWithOrder() {
      return (
        this.item.status === "selected" &&
        typeof this.item.selected_order_uuid === "string" &&
        this.item.selected_order_uuid.trim().length > 0
      );
    },
    // 已使用／已失效改用戳章；已選用及新版尚未開始保留文字。
    hasStatusNote() {
      return !["可使用", "已使用", "已失效"].includes(this.getStatusText);
    },
    getValidFromText() {
      return formatCouponDateTime(getCouponValidFromAt(this.item), this.item.timezone_name);
    },
    getValidUntilText() {
      return formatCouponDateTime(getCouponValidUntilAt(this.item), this.item.timezone_name);
    },
  },
  methods: {
    showMemberDetail() {
      if (this.canShowDetail) {
        this.$emit("showMemberDetail", this.item.member_coupon_uuid);
      }
    },
    onCardClick(event) {
      if (!event.target.closest("button")) this.showMemberDetail();
    },
    onCardKeydown(event) {
      if (
        event.target !== event.currentTarget ||
        !["Enter", " "].includes(event.key)
      ) return;
      event.preventDefault();
      this.showMemberDetail();
    },
  },
};
</script>
<style scoped>
.member_coupon {
  box-sizing: border-box;
  width: 100%;
  position: relative;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px dashed #cfcfcf;
  margin-bottom: 10px;
}
.member_coupon_content {
  min-width: 0;
  flex: 1;
}
.is_clickable {
  cursor: pointer;
}
.is_clickable:focus-visible {
  outline: 2px solid #fd5001;
  outline-offset: -2px;
}
.member_coupon_content h3 {
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 600;
  color: #000;
  overflow-wrap: anywhere;
}
.member_coupon_content p {
  margin: 4px 0;
}
.coupon_status_note {
  font-size: 13px;
  color: #666;
  overflow-wrap: anywhere;
}
.coupon_period {
  font-size: 12px;
  color: #a45721;
}
.coupon_action {
  align-self: center;
  flex-shrink: 0;
  max-width: 42%;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #fd5001;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}
.coupon_used {
  background: #c4c8cf;
  color: #4b5563;
}
.coupon_used :deep(.coupon_image_thumbnail) {
  filter: grayscale(1);
  opacity: 0.6;
}
.coupon_used .member_coupon_content h3,
.coupon_used .coupon_period {
  color: #4b5563;
}
.used_stamp {
  width: 88px;
  height: auto;
  flex-shrink: 0;
  align-self: center;
  pointer-events: none;
}
button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #ddd;
  color: #777;
}
</style>
